/**
 * Sitemap panel — lives under Admin → SEO.
 *
 * - Shows the last snapshot (URL count, generated_at, base URL used)
 * - "Generate Now" hits POST /api/admin/sitemap/generate
 * - Scheduler block (enable/frequency/day/hour) persists via
 *   PUT /api/admin/sitemap/scheduler and the shared APScheduler runs it
 *   hourly at :10 UTC.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw, FileText, ExternalLink, Clock, CheckCircle2 } from "lucide-react";

const API = getApiUrl();
const FREQS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function SitemapPanel() {
  const [status, setStatus] = useState({ snapshot: {}, scheduler: {} });
  const [scheduler, setScheduler] = useState({
    enabled: false,
    frequency: "weekly",
    hour: 3,
    day_of_week: 1,
    day_of_month: 1,
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingSched, setSavingSched] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statusRes, schedRes] = await Promise.all([
        axios.get(`${API}/admin/sitemap/status`, { withCredentials: true }),
        axios.get(`${API}/admin/sitemap/scheduler`, { withCredentials: true }),
      ]);
      setStatus(statusRes.data || { snapshot: {}, scheduler: {} });
      setScheduler({
        enabled: !!schedRes.data?.enabled,
        frequency: schedRes.data?.frequency || "weekly",
        hour: schedRes.data?.hour ?? 3,
        day_of_week: schedRes.data?.day_of_week ?? 1,
        day_of_month: schedRes.data?.day_of_month ?? 1,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sitemap status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/sitemap/generate`,
        {},
        { withCredentials: true }
      );
      toast.success(`Sitemap generated — ${data.url_count} URLs`);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate sitemap");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveScheduler = async () => {
    setSavingSched(true);
    try {
      await axios.put(
        `${API}/admin/sitemap/scheduler`,
        scheduler,
        { withCredentials: true }
      );
      toast.success("Sitemap schedule saved");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSavingSched(false);
    }
  };

  const snap = status.snapshot || {};
  const sched = status.scheduler || {};
  const publicUrl = `${window.location.origin}/api/sitemap.xml`;

  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading sitemap…
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-md p-6 space-y-5" data-testid="sitemap-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-primary mt-1" />
          <div>
            <h3 className="font-serif text-lg font-medium">Sitemap</h3>
            <p className="text-xs text-muted-foreground">
              Auto-collects home, modular pages, custom pages, live properties
              and blog articles.
            </p>
          </div>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={generating}
          data-testid="sitemap-generate-now"
        >
          {generating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Generate now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="border border-border rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            URLs indexed
          </div>
          <div className="text-2xl font-medium" data-testid="sitemap-url-count">
            {snap.url_count ?? 0}
          </div>
        </div>
        <div className="border border-border rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Last generated
          </div>
          <div className="text-sm" data-testid="sitemap-generated-at">
            <Clock className="inline h-3 w-3 mr-1 text-muted-foreground" />
            {fmt(snap.generated_at)}
          </div>
          {snap.triggered_by && (
            <div className="text-[10px] text-muted-foreground mt-1">
              via {snap.triggered_by}
            </div>
          )}
        </div>
        <div className="border border-border rounded p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
            Public URL
          </div>
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1 break-all"
            data-testid="sitemap-public-link"
          >
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
            <span>/api/sitemap.xml</span>
          </a>
        </div>
      </div>

      {/* Search-engine ping status */}
      {snap.pings && Object.keys(snap.pings).length > 0 && (
        <div className="border border-border rounded p-3 space-y-2" data-testid="sitemap-pings">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Search-engine ping (auto-fires after every generation)
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(snap.pings).map(([engine, res]) => (
              <div
                key={engine}
                className="flex items-center justify-between rounded border border-border px-2 py-1"
                data-testid={`sitemap-ping-${engine}`}
              >
                <span className="font-medium capitalize">{engine}</span>
                <span
                  className={
                    res.ok
                      ? "text-emerald-700 inline-flex items-center gap-1"
                      : "text-amber-700 inline-flex items-center gap-1"
                  }
                  title={res.error || res.url}
                >
                  {res.ok ? (
                    <CheckCircle2 className="h-3 w-3" />
                  ) : (
                    <Clock className="h-3 w-3" />
                  )}
                  HTTP {res.status || "—"}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground">
            Note: Google (Jun 2023) and Bing (May 2022) officially retired
            these ping endpoints. We still fire them because the URLs remain
            reachable and legacy crawlers use the same convention — but if you
            want guaranteed refresh, submit the sitemap URL in Google Search
            Console / Bing Webmaster Tools once.
          </p>
        </div>
      )}

      {/* Scheduler */}
      <div className="border border-border rounded p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Auto-regenerate schedule</Label>
            <p className="text-[11px] text-muted-foreground">
              Regenerates the sitemap on a cron so it always mirrors the latest
              properties and articles.
            </p>
          </div>
          <Switch
            checked={scheduler.enabled}
            onCheckedChange={(v) => setScheduler((s) => ({ ...s, enabled: v }))}
            data-testid="sitemap-schedule-enabled"
          />
        </div>
        {scheduler.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Frequency</Label>
              <select
                value={scheduler.frequency}
                onChange={(e) => setScheduler((s) => ({ ...s, frequency: e.target.value }))}
                className="w-full h-9 px-2 border border-input rounded bg-background text-sm"
                data-testid="sitemap-schedule-frequency"
              >
                {FREQS.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hour (UTC)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={scheduler.hour}
                onChange={(e) => setScheduler((s) => ({ ...s, hour: Number(e.target.value) }))}
                data-testid="sitemap-schedule-hour"
              />
            </div>
            {scheduler.frequency === "weekly" && (
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Day of week</Label>
                <div className="flex gap-1">
                  {DAYS.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setScheduler((s) => ({ ...s, day_of_week: i }))}
                      className={`flex-1 h-8 text-xs rounded border ${
                        scheduler.day_of_week === i
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {scheduler.frequency === "monthly" && (
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Day of month (1-28)</Label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={scheduler.day_of_month}
                  onChange={(e) => setScheduler((s) => ({ ...s, day_of_month: Number(e.target.value) }))}
                />
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between pt-2">
          {sched.next_run_at ? (
            <p className="text-[11px] text-muted-foreground">
              Next auto-run: <span className="font-medium">{fmt(sched.next_run_at)}</span>
              {sched.last_run_at && (
                <> · last: {fmt(sched.last_run_at)}</>
              )}
            </p>
          ) : <span />}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveScheduler}
            disabled={savingSched}
            data-testid="sitemap-schedule-save"
          >
            {savingSched ? (
              <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3 w-3 mr-2" />
            )}
            Save schedule
          </Button>
        </div>
      </div>
    </div>
  );
}
